import React from "react"

// Models
import {Signal} from "../records/models"
import {Beat, orderProblems} from "./model"

import * as store from "./storage"
import AnnotationService from "./service/AnnotationService"
import useAnnotation, {AnnotationApi} from "./useAnnotation"
import useAutosave, {SaveOutcome} from "./useAutosave"
import useRecordLabels, {LabelsStatus} from "./labels"
import {AnnotationSaveDto, toDto} from "./remote"
import {recordEndMs, recordSampleRate, RecordRate} from "./sampleRate"

type Subscribable = {subscribe(next: (value: any) => void, error: (error: any) => void): {unsubscribe(): void}};

export type RecordAnnotation = {
    annotation: AnnotationApi
    /** The record's own sample rate, or why there is none. */
    rate: RecordRate
    /** The server's rate, when it sent one (and it disagreed, see labelsStatus). */
    serverRate: number | null
    labelsStatus: LabelsStatus
    /** Marks loaded and editable: rate known, labels in, beats this record's. */
    ready: boolean
    /**
     * Beats where this browser's unsynced copy was older than the server's —
     * the server's kept. Non-zero means some earlier work here was not applied.
     */
    conflicts: number
    savedAt: string | null
    savedTo: "server" | "local" | null
    /** The browser refused the backup copy. */
    localFailed: boolean
    /** Edits the server does not have yet (or a save in flight). */
    unsaved: boolean
    /** The last save to the server failed; retrying. */
    failing: boolean
    /** The server refused the last save (status in saveStatus); not retried until the next edit. */
    refused: boolean
    saveStatus: number | null
    flush(): void
    /** Beats a person touched that still break the order. */
    outOfOrderReviewed: number
}

/** One empty list, so a render while labels load never reads as new labels. */
const NO_BEATS: Beat[] = [];

/**
 * A failed request, by its status: try again, or the server said no.
 *
 * Network errors (status 0), 5xx, 408 and 429 may pass if tried again;
 * any other status is the server refusing these beats, and sending the same
 * body again will be refused the same way.
 */
export function outcomeOf(error: any): SaveOutcome {
    const status = error && typeof error.status === "number" ? error.status : 0;

    return status === 0 || status >= 500 || status === 408 || status === 429 ? "retry" : "refused"
}

/*
 * One request per ECG at a time, page-wide, and never the same body twice.
 *
 * Kept outside any hook instance, so however many triggers ask — or however
 * many instances there are — a body already queued or in flight for an ECG
 * is not sent again, and different bodies go one after the other in order.
 */
const queues: {[ecgId: string]: {body: string, outcome: Promise<SaveOutcome>}} = {};

export function sendOnce(ecgId: string, body: string, send: () => Promise<SaveOutcome>): Promise<SaveOutcome> {
    const last = queues[ecgId];

    if (last && last.body === body) {
        return last.outcome
    }

    /* nothing queued: send now; otherwise after the one before it lands */
    const outcome = last ? last.outcome.then(() => send(), () => send()) : send();
    const entry = {body: body, outcome: outcome};

    queues[ecgId] = entry;

    /* once it has landed, the same body may be sent again later (a retry) */
    outcome.then(() => {
        if (queues[ecgId] === entry) {
            delete queues[ecgId]
        }
    }, () => undefined);

    return outcome
}

/**
 * A record's annotation, from the server and back, at the record's own rate.
 *
 * The rate comes from the record's signals and nowhere else; until every lead
 * agrees on one, nothing is fetched, shown for editing, or saved. Labels are
 * settled once per record AND rate, so a rate that changes re-seeds instead of
 * keeping marks converted at the old one — and the beats are saved only once
 * they are the ones loaded for this record and rate.
 *
 * @param input
 */
export default function useRecordAnnotation(input: {
    recordKey: string | null
    ecgId: string | null
    recordName: string | null
    signals: Signal[] | null
    /** annotation mode is on: the keyboard is the annotation's */
    on: boolean
    /** for tests: the GET and the PUT */
    fetch?: (ecgId: string) => Subscribable
    send?: (ecgId: string, dto: AnnotationSaveDto) => Subscribable
}): RecordAnnotation {
    const {recordKey, ecgId, recordName, signals, on} = input;

    const rate = React.useMemo(() => recordSampleRate(signals), [signals]);
    const sampleRate = rate.rate;
    const endMs = React.useMemo(() => recordEndMs(signals, sampleRate), [signals, sampleRate]);

    const labels = useRecordLabels(ecgId, sampleRate, input.fetch);
    const freshLabels = labels.status === "ready" ? labels.beats : NO_BEATS;

    const seedKey = recordKey && sampleRate ? `${recordKey}@${sampleRate}` : null;

    /*
     * Settled ONCE per record and rate, and held by reference afterwards.
     *
     * `useAnnotation` resets everything — beats, cursor, undo — whenever the
     * array it is handed changes identity. Recomputing this on every change of
     * the labels meant an ordinary update of the signals threw the technician
     * back to beat 1 and cleared the undo history.
     */
    const seededRef = React.useRef<{key: string | null, beats: Beat[], conflicts: number}>(
        {key: null, beats: NO_BEATS, conflicts: 0},
    );

    const seeded = React.useMemo(() => {
        if (!seedKey || labels.status !== "ready") {
            return {key: seedKey, beats: NO_BEATS, conflicts: 0}
        }

        if (seededRef.current.key === seedKey && seededRef.current.beats.length) {
            return seededRef.current
        }

        /*
         * This browser's copy counts only while it never reached the server,
         * only at this rate, and only for beats the server has not changed
         * since (store.merge).
         */
        const stored = recordKey ? store.load(recordKey) : null;
        const unsynced = stored && !stored.synced && stored.sampleRate === sampleRate ? stored : null;
        const merged = store.merge(freshLabels, unsynced);
        const next = {key: seedKey, beats: merged.beats, conflicts: merged.conflicts};

        /* only latch once there is something to latch */
        if (next.beats.length) {
            seededRef.current = next
        }

        return next
    }, [seedKey, recordKey, sampleRate, freshLabels, labels.status]);

    const ready = sampleRate !== null && labels.status === "ready";

    const annotation = useAnnotation(seeded.beats, sampleRate || 0, {
        enabled: on && ready,
        origin: freshLabels,
        maxMs: endMs,
    });

    /* the beats on screen are the ones loaded for THIS record and rate */
    const ours = ready && annotation.loadedFrom === seeded.beats && seeded.beats.length > 0;

    const [savedAt, setSavedAt] = React.useState<string | null>(null);
    const [savedTo, setSavedTo] = React.useState<"server" | "local" | null>(null);
    const [localFailed, setLocalFailed] = React.useState(false);
    const [saveStatus, setSaveStatus] = React.useState<number | null>(null);

    React.useEffect(() => {
        setSavedAt(null);
        setSavedTo(null);
        setLocalFailed(false);
        setSaveStatus(null)
    }, [recordKey]);

    /* the server announced its rate on GET: it takes, and checks, sampleRate on PUT */
    const announceRate = labels.serverRate !== null;

    const sendRef = React.useRef(input.send || ((id: string, dto: AnnotationSaveDto) => AnnotationService.saveAnnotation(id, dto)));

    /**
     * Save: the browser copy first (synchronously, so a closed tab keeps it),
     * then the server. Resolves true once the server has it.
     */
    const save = React.useCallback((beats: Beat[]): Promise<SaveOutcome> => {
        if (!recordKey || !sampleRate) {
            return Promise.resolve<SaveOutcome>("refused")
        }

        const copy: store.Saved = {
            recordName: recordName || recordKey,
            sampleRate: sampleRate,
            beats: beats,
            savedAt: new Date().toISOString(),
            base: store.baseOf(freshLabels),
        };
        const ok = store.save(recordKey, copy);

        setLocalFailed(!ok);
        setSavedAt(ok ? new Date().toLocaleTimeString() : null);
        setSavedTo(ok ? "local" : null);

        if (!ecgId) {
            return Promise.resolve<SaveOutcome>(ok ? "saved" : "retry")
        }

        const dto = toDto(beats, sampleRate, announceRate);

        return sendOnce(ecgId, JSON.stringify(dto), () => new Promise<SaveOutcome>(resolve => {
            sendRef.current(ecgId, dto).subscribe(
                () => {
                    store.save(recordKey, Object.assign({}, copy, {synced: true}));
                    setLocalFailed(false);
                    setSaveStatus(null);
                    setSavedAt(new Date().toLocaleTimeString());
                    setSavedTo("server");
                    resolve("saved")
                },
                /* the browser copy stands; autosave retries, or not, by status */
                error => {
                    setSaveStatus(error && typeof error.status === "number" ? error.status : 0);
                    setSavedTo(ok ? "local" : null);
                    resolve(outcomeOf(error))
                },
            )
        }))
    }, [recordKey, ecgId, recordName, sampleRate, freshLabels, announceRate]);

    const autosave = useAutosave(annotation.beats, freshLabels, ours, save, seedKey);

    const outOfOrderReviewed = React.useMemo(
        () => (ours
            ? annotation.beats.filter((b, i, all) => b.status !== "auto"
                && orderProblems(b, all[i + 1] || null, all[i - 1] || null).length > 0).length
            : 0),
        [annotation.beats, ours],
    );

    return {
        annotation: annotation,
        rate: rate,
        serverRate: labels.serverRate,
        labelsStatus: labels.status,
        ready: ready,
        conflicts: seeded.conflicts,
        savedAt: savedAt,
        savedTo: savedTo,
        localFailed: localFailed,
        unsaved: autosave.unsaved,
        failing: autosave.failing,
        refused: autosave.refused,
        saveStatus: saveStatus,
        flush: autosave.flush,
        outOfOrderReviewed: outOfOrderReviewed,
    }
}
